// ============================================================
// systems/menu.js — Loading screen, main menu, character select
// ============================================================
import { state } from '../state.js';
import { dom, defaultPlushieSrc, closeAllPanels } from '../dom.js';
import { CHARACTER_IMAGES, LOADING_CHARS } from '../constants.js';
import { updateBadgesUI } from './win.js';
import { saveGame } from './save.js';
import { startCurrentMusic } from '../audio/manager.js';
import { playPanelOpenSound, playPanelCloseSound } from '../audio/sfx.js';
import { isLoggedIn, showAuthModal } from './auth.js';

// Forward-declared functions — set via provideMenuDeps()
let resetGame;
let deactivatePhase5;
let resetLives;
let showLivesUI;
let stopShieldSpawning;
let startShieldSpawning;

/**
 * Provide external functions that menu.js needs to call.
 * Called once at bootstrap to break circular dependency chains.
 */
export function provideMenuDeps(deps) {
  resetGame          = deps.resetGame;
  deactivatePhase5   = deps.deactivatePhase5;
  resetLives         = deps.resetLives;
  showLivesUI        = deps.showLivesUI;
  stopShieldSpawning = deps.stopShieldSpawning;
  startShieldSpawning = deps.startShieldSpawning;
}

// ---- Character image helper ----

export function setCharacterImage(charName) {
  const img = dom.smiley.querySelector('img');
  if (CHARACTER_IMAGES[charName]) {
    img.src = CHARACTER_IMAGES[charName];
  } else {
    img.src = defaultPlushieSrc;
  }
}

// ---- Game UI visibility ----

export function setGameUIVisible(visible) {
  const v = visible ? '' : 'none';
  dom.shopBtn.style.display = v;
  dom.badgeBtn.style.display = v;
  dom.pauseBtn.style.display = v;
  dom.settingsBtn.style.display = v;
  dom.scoreDisplay.style.display = v;
  dom.clickPowerDisplay.style.display = v;
  dom.smiley.style.display = v;
}

// ---- Go to menu ----

export function goToMenu() {
  state.inMenu = true;
  document.body.classList.remove('competitive-mode');
  setGameUIVisible(false);
  showLivesUI(false);
  dom.winScreen.classList.remove('show');
  dom.gameOverEl.classList.remove('show');
  dom.charSelect.classList.remove('show');
  dom.modeSelect.classList.remove('show');
  dom.continueOverlay.classList.remove('show');
  dom.settingsEl.classList.remove('open');
  dom.badgePanel.classList.remove('open');
  dom.shopEl.classList.remove('open');
  dom.mainMenu.classList.add('show');
}

// ---- Init all menu event listeners ----

export function initMenu() {
  // Random character on loading screen
  dom.loadingCharImg.src = LOADING_CHARS[Math.floor(Math.random() * LOADING_CHARS.length)];

  // Hide game UI while in menu
  setGameUIVisible(false);

  // Loading bar animation
  const loadInterval = setInterval(() => {
    state.loadProgress += Math.random() * 8 + 2;
    if (state.loadProgress >= 100) {
      state.loadProgress = 100;
      clearInterval(loadInterval);
      dom.loadingBar.style.width = '100%';
      setTimeout(() => {
        dom.loadingScreen.classList.add('hidden');
        dom.mainMenu.classList.add('show');
      }, 400);
    } else {
      dom.loadingBar.style.width = state.loadProgress + '%';
    }
  }, 80);

  // Menu: Play — show continue overlay if normal game in progress, else mode select
  dom.menuPlayBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.mainMenu.classList.remove('show');
    dom.settingsEl.classList.remove('open');
    dom.badgePanel.classList.remove('open');
    dom.creditsOverlay.classList.remove('show');

    if (state.score > 0 && state.gameMode === 'normal') {
      // Show continue overlay
      dom.continueMain.style.display = '';
      dom.continueConfirm.style.display = 'none';
      dom.continueOverlay.classList.add('show');
    } else {
      dom.modeSelect.classList.add('show');
    }
  });

  // Continue: Same Character — show the game paused, unfreeze on first plushie click
  dom.continueSameBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.continueOverlay.classList.remove('show');
    state.inMenu = false;
    setGameUIVisible(true);
    dom.pauseBtn.style.display = '';
    dom.saveMenuBtn.style.display = '';
    dom.smiley.style.pointerEvents = '';
    // Freeze until first plushie click
    state.paused = true;
    state.awaitingFirstClick = true;
    document.body.classList.add('awaiting-first-click');
  });

  // Continue: Other Character — show confirmation
  dom.continueOtherBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.continueMain.style.display = 'none';
    dom.continueConfirm.style.display = '';
  });

  // Confirm: Go Back — return to continue overlay
  dom.confirmGoBackBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.continueConfirm.style.display = 'none';
    dom.continueMain.style.display = '';
  });

  // Confirm: Proceed — reset and show mode select
  dom.confirmProceedBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.continueOverlay.classList.remove('show');
    resetGame();
    dom.modeSelect.classList.add('show');
  });

  // Mode: Normal
  dom.modeNormal.addEventListener('click', (e) => {
    e.stopPropagation();
    state.gameMode = 'normal';
    document.body.classList.remove('competitive-mode');
    dom.modeSelect.classList.remove('show');
    showLivesUI(false);
    dom.pauseBtn.style.display = '';
    dom.saveMenuBtn.style.display = '';
    dom.saveMenuBtn.textContent = 'Save & Menu';
    stopShieldSpawning();
    state.inMenu = false;
    setGameUIVisible(true);
    dom.charSelect.classList.add('show');
  });

  // Mode: Competitive — prompt auth if not logged in
  function proceedToCompetitive() {
    state.gameMode = 'competitive';
    document.body.classList.add('competitive-mode');
    // Competitive always starts fresh — reset score and shop
    resetGame();
    resetLives();
    dom.modeSelect.classList.remove('show');
    showLivesUI(true);
    dom.pauseBtn.style.display = '';
    dom.saveMenuBtn.style.display = '';
    dom.saveMenuBtn.textContent = 'Exit to Menu';
    dom.highScoreVal.textContent = state.highScore;
    startShieldSpawning();
    state.inMenu = false;
    setGameUIVisible(true);
    dom.charSelect.classList.add('show');
  }

  dom.modeCompetitive.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!isLoggedIn()) {
      showAuthModal(
        'Sign in to save your competitive score to the leaderboard!',
        () => proceedToCompetitive()
      );
      return;
    }
    proceedToCompetitive();
  });

  // Menu: Settings — toggle (click again to close)
  dom.menuSettingsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.settingsEl.classList.contains('open');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      dom.settingsEl.classList.add('open');
      playPanelOpenSound();
    }
  });

  // Menu: Badges — toggle
  dom.menuBadgesBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.badgePanel.classList.contains('open');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      updateBadgesUI();
      dom.badgePanel.classList.add('open');
      playPanelOpenSound();
    }
  });

  // Menu: Credits — toggle
  dom.menuCreditsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.creditsOverlay.classList.contains('show');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      dom.creditsOverlay.classList.add('show');
      playPanelOpenSound();
    }
  });

  // Credits: Close
  dom.creditsCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.creditsOverlay.classList.remove('show');
    playPanelCloseSound();
  });

  // In-game settings button
  dom.settingsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.settingsEl.classList.contains('open');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      dom.settingsEl.classList.add('open');
      playPanelOpenSound();
    }
  });
  dom.settingsCloseBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.settingsEl.classList.remove('open');
    playPanelCloseSound();
  });

  // In-game badge button
  dom.badgeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const wasOpen = dom.badgePanel.classList.contains('open');
    closeAllPanels();
    if (wasOpen) {
      playPanelCloseSound();
    } else {
      updateBadgesUI();
      dom.badgePanel.classList.add('open');
      playPanelOpenSound();
    }
  });
  dom.badgeClose.addEventListener('click', (e) => {
    e.stopPropagation();
    dom.badgePanel.classList.remove('open');
    playPanelCloseSound();
  });

  // ---- Character selection cards ----
  dom.charCards.forEach(card => {
    card.addEventListener('click', (e) => {
      e.stopPropagation();
      state.selectedCharacter = card.dataset.char;
      dom.charSelect.classList.remove('show');
      if (state.charSelectFromWin) {
        state.charSelectFromWin = false;
        state.escaped = false;
        deactivatePhase5();
        resetGame();
      } else {
        state.escaped = false;
        state.paused = false;
        state.dead = false;
        dom.smiley.style.pointerEvents = '';
        dom.smiley.style.display = '';
        setCharacterImage(state.selectedCharacter);
        saveGame();
      }
      // Start music if toggle is on
      if (dom.toggleMusic.classList.contains('on')) {
        startCurrentMusic();
      }
    });
  });

  // ---- Waine locked character ----
  if (dom.charWaineSelect) {
    dom.charWaineSelect.addEventListener('click', (e) => {
      if (!state.caineUnlocked) {
        e.stopPropagation();
        e.preventDefault();
        const msg = document.createElement('div');
        msg.className = 'char-locked-msg';
        msg.innerHTML = '\uD83D\uDD12 Escape the circus with all the plushies!<span>Complete all phases to unlock Waine</span>';
        document.body.appendChild(msg);
        setTimeout(() => msg.remove(), 2500);
      }
    }, true); // capture phase so it runs before the card handler
  }

  // ---- Secret character ----
  if (dom.charSecretSelect) {
    dom.charSecretSelect.addEventListener('click', (e) => {
      if (!state.secretUnlocked) {
        e.stopPropagation();
        e.preventDefault();
        const msg = document.createElement('div');
        msg.className = 'char-locked-msg';
        msg.innerHTML = '\uD83D\uDD12 A mysterious friend awaits...<span>Escape with every character (including Waine) to unlock</span>';
        document.body.appendChild(msg);
        setTimeout(() => msg.remove(), 2500);
      }
    }, true);
  }
}
